import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Loader2Icon,
  PauseIcon,
  PlayIcon,
  SendHorizonalIcon,
  XIcon,
} from 'lucide-react';
import { toast } from 'sonner';

import { generateTextToSpeech } from '@/hooks/contact';
import { Button } from '@/components/ui/button';

const TEXT_TO_SPEECH_STATES = {
  IDLE: 'idle',
  GENERATING: 'generating',
  REVIEW: 'review',
  PROCESSING: 'processing',
};

export default function TextToSpeech({
  text,
  onComplete,
  onCancel,
  setIsTextToSpeechActive,
}) {
  const [state, setState] = useState(TEXT_TO_SPEECH_STATES.IDLE);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioMimeType, setAudioMimeType] = useState(null);
  const [audioExtension, setAudioExtension] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);

  const audioRef = useRef(null);
  const isFirstRender = useRef(true);

  // Notify parent when state changes (skip first render to prevent unmounting)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const isActive = state !== TEXT_TO_SPEECH_STATES.IDLE;
    setIsTextToSpeechActive?.(isActive);
  }, [state, setIsTextToSpeechActive]);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (audioUrl && audioUrl.startsWith('blob:')) {
      URL.revokeObjectURL(audioUrl);
    }
  }, [audioUrl]);

  const resetToIdle = useCallback(() => {
    cleanup();
    setState(TEXT_TO_SPEECH_STATES.IDLE);
    setAudioUrl(null);
    setAudioBlob(null);
    setAudioMimeType(null);
    setAudioExtension(null);
    setIsPlaying(false);
    setDuration(0);
  }, [cleanup]);

  // Format time for display
  const formatTime = useCallback((milliseconds) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  const downloadAudioForPreview = useCallback(async (originalUrl) => {
    try {
      const audioResponse = await fetch(originalUrl);

      if (!audioResponse.ok) {
        throw new Error(
          `Failed to download audio file: ${audioResponse.status} ${audioResponse.statusText}`
        );
      }

      const blob = await audioResponse.blob();
      setAudioBlob(blob);

      // Create a blob URL for preview
      const blobUrl = URL.createObjectURL(blob);
      setAudioUrl(blobUrl);

      return blob;
    } catch (error) {
      console.error('Error downloading audio for preview:', error);
      throw error;
    }
  }, []);

  const generateAudio = useCallback(async () => {
    if (!text || !text.trim()) {
      toast.error('Please enter text to convert to speech');
      return;
    }

    setState(TEXT_TO_SPEECH_STATES.GENERATING);

    try {
      const response = await generateTextToSpeech(text);

      if (response?.audio?.url) {
        // Store audio metadata from response
        setAudioMimeType(response.audio.mime_type || 'audio/mpeg');
        setAudioExtension(response.audio.extension || 'mp4');

        // Download the audio immediately for preview
        await downloadAudioForPreview(response.audio.url);

        setState(TEXT_TO_SPEECH_STATES.REVIEW);
      } else {
        throw new Error('No audio URL in response');
      }
    } catch (error) {
      console.error('Error generating text-to-speech:', error);
      toast.error(
        error.response?.data?.message || 'Failed to generate text-to-speech'
      );
      resetToIdle();
      onCancel?.();
    }
  }, [text, onCancel, resetToIdle, downloadAudioForPreview]);

  // Cancel generation/review
  const cancel = useCallback(() => {
    resetToIdle();
    onCancel?.();
  }, [resetToIdle, onCancel]);

  // Send audio
  const sendAudio = useCallback(async () => {
    if (!audioBlob) {
      toast.error('Audio blob not available');
      return;
    }

    setState(TEXT_TO_SPEECH_STATES.PROCESSING);

    try {
      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, '-')
        .slice(0, -5);

      const fileExtension = audioExtension || 'mp4';
      const mimeType = audioMimeType || 'audio/mpeg';
      const filename = `text-to-speech-${timestamp}.${fileExtension}`;

      const fileWithName = new File([audioBlob], filename, {
        type: mimeType,
      });

      await onComplete(fileWithName);

      // After successful send, reset to idle state
      resetToIdle();
    } catch (error) {
      console.error('Error sending audio:', error);
      toast.error(error.message || 'Failed to send audio message');
      setState(TEXT_TO_SPEECH_STATES.REVIEW);
    }
  }, [audioBlob, audioExtension, audioMimeType, onComplete, resetToIdle]);

  // Play/pause preview
  const togglePreview = useCallback(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);

  // Handle audio ended
  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // Load audio metadata when audioUrl changes
  useEffect(() => {
    if (!audioUrl || state !== TEXT_TO_SPEECH_STATES.REVIEW) return;

    let cleanup = null;

    const timeoutId = setTimeout(() => {
      if (audioRef.current) {
        const audio = audioRef.current;

        const handleLoadedMetadata = () => {
          if (audio.duration && isFinite(audio.duration)) {
            setDuration(audio.duration * 1000); // Convert to milliseconds
          }
        };

        const handleError = (e) => {
          console.error('Error loading audio:', e);
          toast.error('Failed to load audio preview');
        };

        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('error', handleError);

        // Load the audio to trigger metadata loading
        audio.load();

        cleanup = () => {
          audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
          audio.removeEventListener('error', handleError);
        };
      }
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      if (cleanup) cleanup();
    };
  }, [audioUrl, state]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Generate audio when component mounts with text
  useEffect(() => {
    if (text && text.trim() && state === TEXT_TO_SPEECH_STATES.IDLE) {
      generateAudio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render generating state
  if (state === TEXT_TO_SPEECH_STATES.GENERATING) {
    return (
      <div className='flex w-full items-center justify-center rounded-lg border bg-muted p-3 animate-in fade-in zoom-in-95'>
        <div className='flex items-center gap-3'>
          <Loader2Icon className='h-5 w-5 animate-spin text-muted-foreground' />
          <span className='text-sm font-medium text-muted-foreground'>
            Generating audio...
          </span>
        </div>
      </div>
    );
  }

  // Render review interface
  if (state === TEXT_TO_SPEECH_STATES.REVIEW) {
    return (
      <div className='flex w-full items-center justify-between rounded-lg border bg-secondary p-3 animate-in fade-in zoom-in-95'>
        <div className='flex items-center gap-2'>
          <Button
            size='sm'
            variant='ghost'
            onClick={togglePreview}
            className='text-primary hover:bg-primary/10'
          >
            {isPlaying ? (
              <PauseIcon className='h-4 w-4' />
            ) : (
              <PlayIcon className='h-4 w-4' />
            )}
          </Button>
          <span className='text-sm font-medium text-primary'>
            {formatTime(duration)}
          </span>
          <Button
            size='icon'
            variant='ghost'
            onClick={cancel}
            className='h-8 w-8 text-muted-foreground hover:text-foreground'
            title='Discard audio'
          >
            <XIcon className='h-4 w-4' />
          </Button>
        </div>

        <audio
          ref={audioRef}
          src={audioUrl}
          onEnded={handleAudioEnded}
          className='hidden'
        />

        <Button size='sm' variant='default' onClick={sendAudio}>
          <SendHorizonalIcon className='mr-1 h-4 w-4' />
          Send
        </Button>
      </div>
    );
  }

  // Render processing state
  if (state === TEXT_TO_SPEECH_STATES.PROCESSING) {
    return (
      <div className='flex w-full items-center justify-center rounded-lg border bg-muted p-3 animate-in fade-in zoom-in-95'>
        <div className='flex items-center gap-3'>
          <Loader2Icon className='h-5 w-5 animate-spin text-muted-foreground' />
          <span className='text-sm font-medium text-muted-foreground'>
            Sending audio...
          </span>
        </div>
      </div>
    );
  }

  return null;
}
